import { RECENT_DAYS, type AttentionReason, type RosterRow } from './overview';

/**
 * What the overview's list shows, and what each row says.
 *
 * The list is the attention queue until the coach taps a tile or types in the
 * search, and then it is that tile's clients or the matches. Tapping filters;
 * it never reorders. Within every view the queue's priority still decides who
 * comes first, so "Scanned recently" lists a reached target above a quiet
 * week, the same as the queue would.
 */
export type OverviewView = 'attention' | 'active' | 'recent' | 'goals';

/** Lowercased, with diacritics folded, so "nystrom" finds "Nyström". */
const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

const byPriorityThenRecency = (a: RosterRow, b: RosterRow) =>
  a.priority - b.priority ||
  (a.daysSinceScan ?? Number.POSITIVE_INFINITY) - (b.daysSinceScan ?? Number.POSITIVE_INFINITY) ||
  a.name.localeCompare(b.name);

const byRecency = (a: RosterRow, b: RosterRow) =>
  (a.daysSinceScan ?? Number.POSITIVE_INFINITY) - (b.daysSinceScan ?? Number.POSITIVE_INFINITY) ||
  a.priority - b.priority ||
  a.name.localeCompare(b.name);

export function rowsForView(
  rows: RosterRow[],
  queue: RosterRow[],
  view: OverviewView,
  query = '',
): RosterRow[] {
  const q = fold(query);
  // A search looks across everyone, whatever tile is pressed: a coach typing
  // a name wants that person, not that person only if they are also overdue.
  if (q) return rows.filter((r) => fold(r.name).includes(q)).sort(byPriorityThenRecency);

  switch (view) {
    case 'attention':
      return queue;
    case 'active':
      return [...rows].sort(byPriorityThenRecency);
    case 'recent':
      return rows
        .filter((r) => r.daysSinceScan !== null && r.daysSinceScan <= RECENT_DAYS)
        .sort(byRecency);
    case 'goals':
      return rows.filter((r) => r.reason === 'goal-reached').sort(byRecency);
  }
}

/** The list's own heading, which says what the tiles filtered it to. */
export const viewTitle = (
  view: OverviewView,
  count: number,
  query: string,
  clients: string,
): string => {
  if (query.trim()) return `Matching “${query.trim()}” · ${count}`;
  switch (view) {
    case 'attention':
      return `Needs your attention · ${count}`;
    case 'active':
      return `All active ${clients} · ${count}`;
    case 'recent':
      return `Scanned in the last ${RECENT_DAYS} days · ${count}`;
    case 'goals':
      return `Targets reached · ${count}`;
  }
};

export const REASON_COPY: Record<AttentionReason, { headline: string; action: string }> = {
  'goal-reached': { headline: 'Target reached', action: 'Set new goal' },
  'scan-to-review': { headline: 'New scan to review', action: 'Review' },
  // The thing to do about a client who has gone quiet, or never started, is
  // to get a scan out of them — so the button says that, rather than "Open".
  overdue: { headline: 'Gone quiet', action: 'Send scan link' },
  'never-scanned': { headline: 'Never scanned', action: 'Send scan link' },
};

/** "Today", "Yesterday", "3 days ago". */
export const daysAgo = (days: number): string =>
  days <= 0 ? 'Today' : days === 1 ? 'Yesterday' : `${days} days ago`;

/** The second line: why this row is here. */
export const headlineFor = (r: RosterRow): string => {
  if (r.reason) return REASON_COPY[r.reason].headline;
  if (r.daysSinceScan === null) return 'Never scanned';
  return `Scanned ${daysAgo(r.daysSinceScan).toLowerCase()}`;
};

/** The third line: the evidence, kept short enough for a phone row. */
export const detailFor = (r: RosterRow, bodyFat: boolean): string => {
  switch (r.reason) {
    case 'goal-reached':
      return r.targetBodyFat !== null && r.nowBodyFat !== null
        ? `Body fat ${r.nowBodyFat.toFixed(1)} % · target ${r.targetBodyFat} %`
        : 'Target reached';
    case 'overdue':
      return `No scan in ${r.daysSinceScan} days`;
    case 'never-scanned':
      return 'Invited, no scan yet';
    default:
      return (
        [
          // One scan means there is no "from" — "34.8 → 34.8 %" reads as a
          // stall rather than as a first reading.
          bodyFat && r.nowBodyFat !== null
            ? r.startBodyFat !== null && r.scanCount > 1
              ? `Body fat ${r.startBodyFat.toFixed(1)} → ${r.nowBodyFat.toFixed(1)} %`
              : `Body fat ${r.nowBodyFat.toFixed(1)} %, first scan`
            : null,
          r.waistCm ? `${bodyFat ? 'waist' : 'Waist'} ${Math.round(r.waistCm)} cm` : null,
        ]
          .filter(Boolean)
          .join(' · ') ||
        (r.scanCount === 0 ? 'No scan yet' : `${r.scanCount} ${r.scanCount === 1 ? 'scan' : 'scans'}`)
      );
  }
};

/** The row's button. Rows outside the queue have nothing waiting: Open. */
export const actionFor = (r: RosterRow): string => (r.reason ? REASON_COPY[r.reason].action : 'Open');
